import { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import Location from "../../hooks/Location";
import { Viewer } from "../../components/liveReport/reportBuilder2/editor/Viewer.jsx";
import { client } from "../../hooks/Client";
import { TemplateDataProvider } from "../../components/liveReport/reportBuilder2/context/TemplateDataContext.jsx";
import { LiveReportCrudContext } from "../../components/liveReport/reportBuilder2/context/LiveReportCrudContext.jsx";
import { ReportPeriodContext } from "../../components/liveReport/reportBuilder2/context/ReportPeriodContext.jsx";
import ReportPersonaContext from "../../StateManagment/ReportPersonaContext";
import ReportPrintContext from "../../StateManagment/ReportPrintContext";
import useLiveReportWidgets from "../../hooks/useLiveReportWidgets.js";
import reportToPeriodRange from "../../helpers/liveReportHelpers/reportToPeriodRange.js";

/**
 * Printvel van een live rapportage: de pagina achter 'PDF maken' in het venster
 * 'Delen'. Dat venster bestaat alleen voor gepubliceerde rapportages, en dit vel
 * houdt dezelfde poort aan, zodat een concept ook via de url geen PDF wordt. Nodig omdat
 * de viewer de vragen van een onderzoek als carrousel toont (één tegelijk) en
 * in de app-omlijsting staat; hier staat alles onder elkaar, zonder menu, en
 * bewaart de gebruiker het vel via het afdrukvenster van de browser als PDF.
 * Zelfde opzet als het printvel van een vragenlijst (PrintQuestionaire).
 */
const LiveReportPrint = () => {

    const history = useHistory();
    const liveReportCrudContext = useContext(LiveReportCrudContext);

    if (!liveReportCrudContext) {
        throw new Error('Missing LiveReportCrudContext.');
    }

    const { report, reportLookup } = liveReportCrudContext;
    const reportId = Location()[3];
    const widgets = useLiveReportWidgets({ reportId, report });
    const periodRange = useMemo(() => reportToPeriodRange(report), [report]);
    // Een rapportage in de prullenbak telt als weg: de provider filtert Deleted niet.
    const missing = reportLookup === 'missing' || report?.Deleted === true;
    const published = !missing && report?.Status === 'published';

    return (
        <div className="livereport-print">
            <div className="livereport-print-toolbar">
                <button
                    type="button"
                    className="template-action-button secondary"
                    onClick={() => history.push(`/${client}/livereports2`)}
                >
                    <ArrowBackIcon />
                    <span>Terug</span>
                </button>
                {published && (
                    <button type="button" className="template-action-button primary" onClick={() => window.print()}>
                        <PictureAsPdfOutlinedIcon />
                        <span>Bewaar als PDF</span>
                    </button>
                )}
            </div>

            {/* Eerst 'weet ik niet', dan 'bestaat niet' of 'mislukt', dan de inhoud.
                AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
            {!report && !missing && reportLookup !== 'error' && (
                <p className="livereport-print-status">Rapportage wordt geladen…</p>
            )}
            {missing && <p className="livereport-print-status">Deze rapportage bestaat niet (meer).</p>}
            {reportLookup === 'error' && !report && (
                <p className="livereport-print-status">De rapportage kon niet worden geladen. Probeer het later opnieuw.</p>
            )}
            {report && !missing && !published && (
                <p className="livereport-print-status">
                    Deze rapportage is nog een concept. Publiceer de rapportage eerst om er een PDF van te maken.
                </p>
            )}
            {published && (
                <div className="livereport-print-sheet">
                    <TemplateDataProvider isTemplate={report.IsTemplate ?? false}>
                        <ReportPeriodContext.Provider value={periodRange}>
                            <ReportPersonaContext.Provider value={report.PersonaID ?? null}>
                                <ReportPrintContext.Provider value={true}>
                                    {widgets && <Viewer widgets={widgets} />}
                                </ReportPrintContext.Provider>
                            </ReportPersonaContext.Provider>
                        </ReportPeriodContext.Provider>
                    </TemplateDataProvider>
                </div>
            )}
        </div>
    );
}

export default LiveReportPrint;
